// @ts-nocheck
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import ReactQuill, { Quill} from 'react-quill-new';
import QuillResizeImage from 'quill-resize-image';

import { useAuth } from '@/auth/clerkShim';
import { styled } from "@mui/material/styles";
import { Button } from "@/components/ui/button";
import PropTypes from 'prop-types';
import axios from 'axios';
import 'react-quill-new/dist/quill.snow.css';
import '@staty/shared/styles/quillContent.css';
import './styles.css';

// Quill 2's stock image format only round-trips `alt`/`height`/`width` —
// inline `style` (float, margin, width%) and any `class` are stripped on
// parse. The resize-image module writes layout via `style`, so without
// this widened whitelist a Save Changes → reopen round-trip silently
// collapses the layout back to a vertical stack while the public site
// still renders the original styled HTML.
const ImageBlot: any = Quill.import('formats/image');
const IMAGE_ATTRIBUTES = ['alt', 'height', 'width', 'style', 'class'];
class StyledImage extends ImageBlot {
  static formats(domNode: HTMLElement) {
    return IMAGE_ATTRIBUTES.reduce((formats: Record<string, string>, attr) => {
      if (domNode.hasAttribute(attr)) formats[attr] = domNode.getAttribute(attr) as string;
      return formats;
    }, {});
  }
  format(name: string, value: string) {
    if (IMAGE_ATTRIBUTES.indexOf(name) > -1) {
      if (value) this.domNode.setAttribute(name, value);
      else this.domNode.removeAttribute(name);
    } else {
      super.format(name, value);
    }
  }
}
StyledImage.blotName = 'image';
StyledImage.tagName = 'IMG';

// Quill 2 has no divider format, so `<hr>` in stored layout HTML is silently
// dropped on load — and the next Save persists the loss. Register a minimal
// block-embed so separators round-trip and render in the editing pane
// (matching what the public site shows).
const BlockEmbed: any = Quill.import('blots/block/embed');
class DividerBlot extends BlockEmbed {
  // Without a truthy value() the clipboard's matchBlot produces an insert of
  // `undefined` and silently drops the embed from the converted Delta.
  static value() {
    return true;
  }
}
DividerBlot.blotName = 'divider';
DividerBlot.tagName = 'HR';

Quill.register('modules/resize', QuillResizeImage);
Quill.register(StyledImage, true);
Quill.register(DividerBlot);
// Quill.register('modules/imageUploader', ImageUploader);



const baseUrl = `${process.env.API_URL}/api/v1`;

const PREFIX = 'PageEditor';
const classes = {
  button: `${PREFIX}-button`,
  input: `${PREFIX}-input`
};


// `contentMaxWidth` caps the writing surface so the author composes at the same
// measure the content will actually render at. Only POST content is capped —
// legacy layout pages render full-width on the public site, so capping their
// editor would mislead in the opposite direction. See HomeEditorAdmin.
const Root = styled('div', {
  shouldForwardProp: (prop) => prop !== 'contentMaxWidth',
})(({ theme, contentMaxWidth }) => ({
  [`& .${classes.button}`]: {
    margin: theme.spacing(1),
  },
  [`& .${classes.input}`]: {
    display: "none",
  },
  // Custom styles for Quill editor
  '& .ql-container': {
    // No fontSize/fontFamily here: content typography is pinned in
    // packages/shared/styles/quillContent.css so this editor and the public site
    // render identically. These two were already inert (the shared file's
    // `!important` on .ql-editor won), but leaving them invited the next person
    // to "fix" typography in one app again.
    border: 'none',
    // The cap lives HERE, not on .ql-editor. quill-resize-image appends its
    // selection overlay to .ql-container but computes the offset against
    // .ql-editor (`new ResizePlugin(target, container.parentElement, container)`
    // — arg 2 is the overlay's parent, arg 3 is the measuring element). The two
    // must therefore share a left edge. Capping .ql-editor with `margin: auto`
    // inset it by half the leftover width and dragged the resize handles and
    // toolbar that far off the image. Padding on .ql-container wouldn't work
    // either: an absolutely positioned child is placed against the PADDING box,
    // so padding shifts the editor but not the overlay. Capping the container
    // keeps .ql-editor flush inside it, so the offset stays 0.
    // `width: 100%` is load-bearing, not redundant: the editor sits in a flex
    // COLUMN, where the cross axis is horizontal — and auto margins on the cross
    // axis switch off the default `align-self: stretch`, collapsing the box to
    // its shrink-to-fit content width (the placeholder wrapped to one character
    // per line). An explicit width makes the used width definite again, so the
    // auto margins simply centre the leftover space.
    ...(contentMaxWidth
      ? { width: '100%', maxWidth: contentMaxWidth, marginLeft: 'auto', marginRight: 'auto' }
      : {}),
  },
  '& .ql-toolbar': {
    border: 'none',
    borderBottom: '1px solid var(--border)',
    backgroundColor: 'var(--muted)',
    padding: '12px 16px',
  },
  '& .ql-editor': {
    // Only min-height is editor chrome. padding/fontSize/lineHeight live in the
    // shared quillContent.css — they were duplicated (and inert) here.
    minHeight: '400px',
  },
  '& .ql-editor.ql-blank::before': {
    color: 'var(--muted-foreground)',
    fontStyle: 'italic',
  },
}));

/* 
 * Simple editor component that takes placeholder text as a prop 
 */
const Editor = ({ currentLayout = {}, onUpdate, onSave, leagueName, contentMaxWidth = null }) => {
  const [editorHtml, setEditorHtml] = useState('');
  const [theme, setTheme] = useState('snow');
  const [isSaving, setIsSaving] = useState(false);
  const { getToken } = useAuth();
  const quillRef = useRef(null);
  
  useEffect(() => {
    const { html, isConverted } = currentLayout;
    setEditorHtml(html || '');
  }, [currentLayout.html]);

  const onSave2 = async () => {
    if (quillRef.current) {
      setIsSaving(true);
      const editor = quillRef.current.getEditor();
      const htmlContent = editor.root.innerHTML;
      console.log('onSave2::htmlContent: ', htmlContent);
      
      try {
        await onSave(htmlContent);
      } catch (error) {
        console.error('Save failed:', error);
      } finally {
        setIsSaving(false);
      }
    }
  }

  const handleChange = (html, delta, source, editor) => {
    console.log('handleChange:: ', html, delta, source, editor);
    if (editor && editor.root) {
      const htmlContent = editor.root.innerHTML;
      console.log('handleChange::htmlContent: ', htmlContent);
      setEditorHtml(htmlContent);
      onUpdate(htmlContent);
    }
  };

  const handleThemeChange = (newTheme) => {
    if (newTheme === "core") newTheme = null;
    setTheme(newTheme);
  };

  const handleToolbarClick = (e) => {
    console.log('handleToolbarClick::e: ', e.target.classList);
    // Check if alignment button was clicked
    if (e.target.classList.contains('ql-fill') || 
        e.target.closest('.ql-fill')) {
      
      setTimeout(() => {
        if (quillRef.current) {
          const editor = quillRef.current.getEditor();
          const selection = editor.getSelection();
          console.log('handleToolbarClick::selection: ', selection);
          
          // Get the HTML content
          const htmlContent = editor.root.innerHTML;
          console.log('handleToolbarClick::htmlContent: ', htmlContent);
          
          if (selection) {
            const format = editor.getFormat(selection);
            console.log('Alignment changed to:', format.align || 'left');
            console.log('Current HTML:', htmlContent);
          }
        }
      }, 10);
    }
  };

  const imageHandler3 = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Validate file size (e.g., 5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('File size must be less than 5MB');
        return;
      }

      try {
        // Resize the image before uploading
        const resizedFile = await resizeImage(file);
        
        const formData = new FormData();
        formData.append('upload', resizedFile);
        const token = await getToken();
        const response = await axios.post(
          `${baseUrl}/organizations/d/${leagueName}/imageupload`,
          formData,
          {
            headers: {
              'Content-Type': `multipart/form-data; boundary=${formData._boundary}`,
              'Authorization': `Bearer ${token}`
            }
          }
        );

        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true);
        const imageUrl = response.data.result.fileUrl;

        quill.insertEmbed(range.index, 'image', imageUrl, 'user');
        quill.setSelection(range.index + 1);
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Failed to upload image. Please try again.');
      }
    };
  };

  const resizeImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const MAX_WIDTH = 1330;
          let width = img.width;
          let height = img.height;

          if (width > MAX_WIDTH) {
            height = Math.round((height * MAX_WIDTH) / width);
            width = MAX_WIDTH;
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(resizedFile);
          }, file.type, 0.9);
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  /* 
  * Quill modules to attach to editor
  */
  const editorModules = {
    toolbar: {
      container: [
        [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
        [{size: ['small', 'medium', 'large', 'huge']}, { 'color': [] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{'list': 'ordered'}, {'list': 'bullet'}, 
        {'indent': '-1'}, {'indent': '+1'}],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        image: imageHandler3
      }
    },
    resize: {
      showSize: true,
      locale: {},
    },
    clipboard: {
      matchVisual: false,
    }
  };

  console.log('PageEditor4::render::editorHtml: ', editorHtml);

  return (
    <Root onClick={handleToolbarClick} contentMaxWidth={contentMaxWidth} className="h-full flex flex-col">
      {/* Editor Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-card max-h-[73px]">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-foreground">Content Editor</h3>
          <div className="text-sm text-muted-foreground">
            {currentLayout.name || 'Untitled Page'}
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="text-sm text-muted-foreground">
            {editorHtml.length > 0 ? `${editorHtml.length} characters` : 'Start typing...'}
          </div>
          <Button
            onClick={onSave2}
            disabled={isSaving}
            className="px-4 py-2"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Editor Content */}
      <div className="flex-1 bg-white">
        <ReactQuill
          ref={quillRef}
          theme={theme}
          onChange={handleChange}
          value={editorHtml}
          modules={editorModules}
          formats={editorFormats}
          bounds={'.app'}
          placeholder={'Start writing your content here...'}
          className="h-full"
        />
      </div>
    </Root>
  );
};



/* 
 * Quill editor formats
 */
const editorFormats = [
  'header', 'font', 'size', 'color',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link', 'image', 'video',
  // <hr> separators in stored layouts (DividerBlot) — omitting a format
  // from this whitelist makes Quill silently strip it on load.
  'divider'
];

/* 
 * PropType validation
 */
Editor.propTypes = {
  placeholder: PropTypes.string,
  leagueName: PropTypes.string.isRequired,
  // CSS length capping the writing surface. Null = full width (layout pages).
  contentMaxWidth: PropTypes.string
};

export default Editor;